/**
 * App-side classification of `slot_assignment_hard_constraint_failed:<codes>`
 * from assign_roster_shift_slot_controlled.
 *
 * Source of truth for "may route to evidence-pending override" vs "must fail hard":
 * - Overridable = compliance/evidence-pending + operational exception policies
 *   (mirrors ShiftCard COMPLIANCE_OVERRIDE_BLOCKER_CODES + seeded
 *   roster_assignment_exception_policies + DISPATCH_EXCEPTION_OVERRIDEABLE_FLAGS).
 * - Hard-never = work rights / overlap / leave / client_no_go / structural validation codes
 *   (mirrors roster_assignment_exception_hard_never_codes after compliance enable).
 *
 * Unknown codes fail closed (not overridable). The controlled approve RPC remains
 * the authoritative second gate when the manager override path is taken.
 */

import { formatEnum } from '@/lib/format';

/** Base codes that must never route to a manager override from the roster UI. */
// Note (P2 2026-08-04): under worker_compliance WARN the SQL hard-blocker
// helper no longer emits non-clinical slot_role, missing slot_capabilities,
// or induction-only first-shift (journey/flag) walls. Clinical-scope roles,
// statutory-floor onboarding, known-bad credentials, and integrity codes remain
// hard. Provider-configurable onboarding is surfaced separately as first-shift
// readiness and can enter the audited manager-exception path. Codes listed here
// stay non-overridable in the UI
// if they still appear.
export const SLOT_ASSIGNMENT_HARD_NEVER_CODES = new Set<string>([
  'overlap',
  'slot_assignment_overlap',
  'leave',
  'client_no_go',
  'slot_candidate_not_feasible',
  'slot_not_found_for_assignment_validation',
  'slot_assignment_validation_missing_scope',
  'slot_assignment_shift_not_found',
  'slot_not_assignable',
  'slot_already_assigned',
  'staff_already_assigned_to_parallel_slot',
  'slot_staff_not_active_for_shift_date',
  'slot_worker_onboarding_blocking_steps_incomplete',
  'slot_worker_work_rights_expired_or_invalid',
  // Known-bad screening is non-overridable — assignment must not be looser
  // than publish (EXPIRED_SCREENING never softens). Missing evidence still
  // uses the MISSING_SCREENING attestation runway via slot_screenings.
  'slot_screenings_invalid_or_expired',
  'slot_agency_staff_requires_provider',
  'slot_internal_staff_cannot_be_agency',
]);

/**
 * Compliance / evidence-pending codes that are provider-overridable per-shift.
 * Kept aligned with ShiftCard's COMPLIANCE_OVERRIDE_BLOCKER_CODES and the
 * platform-default MANAGER_APPROVAL policy seeds.
 */
export const SLOT_ASSIGNMENT_COMPLIANCE_OVERRIDE_CODES = [
  'slot_role',
  'slot_classification',
  'slot_capabilities',
  'slot_certifications',
  'slot_certifications_invalid_or_expired',
  'slot_training',
  'slot_training_invalid_or_expired',
  // Missing screening evidence only — invalid/expired is hard-never above.
  'slot_screenings',
  'required_role_certification_missing_or_expired',
  'required_role_certification_invalid_or_expired',
  'enforced_policy_acknowledgement_required',
  'slot_worker_first_shift_readiness_not_ready',
] as const;

/**
 * Operational exception codes (availability / fatigue) that the exceptional-
 * assignment RPC may accept with a manager reason + declaration.
 */
export const SLOT_ASSIGNMENT_OPERATIONAL_OVERRIDE_CODES = [
  'availability_exception',
  'availability_pattern',
  'legacy_availability',
  'slot_worker_unavailable',
  'rest_window',
  'slot_assignment_ten_hour_break_before',
  'slot_assignment_ten_hour_break_after',
  'weekly_hours',
  'slot_assignment_weekly_hours_exceed_38',
  'slot_assignment_daily_hours_exceed_12',
] as const;

export const SLOT_ASSIGNMENT_OVERRIDABLE_CODES = new Set<string>([
  ...SLOT_ASSIGNMENT_COMPLIANCE_OVERRIDE_CODES,
  ...SLOT_ASSIGNMENT_OPERATIONAL_OVERRIDE_CODES,
]);

/**
 * Authoritative gates that are not projected by shift_feasibility_mv and may
 * therefore need a reactive review affordance after the controlled plain
 * assignment RPC rejects. Keep this deliberately narrow: general compliance
 * or structural codes must not acquire an override path from app-side truth.
 */
const REACTIVE_MANAGER_REVIEW_CANDIDATE_CODES = new Set<string>([
  'slot_worker_first_shift_readiness_not_ready',
]);

const SLOT_ASSIGNMENT_BLOCKER_LABELS: Record<string, string> = {
  policy_first_shift_override_required: 'Manager override required before first shift',
  slot_role: 'Role',
  slot_classification: 'Classification',
  slot_capabilities: 'Capabilities',
  slot_certifications: 'Certifications',
  slot_certifications_invalid_or_expired: 'Certifications (invalid/expired)',
  slot_training: 'Mandatory training',
  slot_training_invalid_or_expired: 'Mandatory training (expired)',
  slot_screenings: 'Screenings',
  slot_screenings_invalid_or_expired: 'Screenings (invalid/expired)',
  required_role_certification_missing_or_expired: 'Role certification',
  required_role_certification_invalid_or_expired: 'Role certification (invalid/expired)',
  enforced_policy_acknowledgement_required: 'Policy acknowledgement',
  slot_worker_first_shift_readiness_not_ready: 'Onboarding readiness',
  slot_worker_work_rights_expired_or_invalid: 'Work rights expired or invalid',
  availability_exception: 'Availability exception',
  availability_pattern: 'Outside availability pattern',
  legacy_availability: 'Availability conflict',
  slot_worker_unavailable: 'Worker marked unavailable',
  rest_window: 'Rest break shortfall',
  weekly_hours: 'Weekly hours limit',
  slot_assignment_ten_hour_break_before: 'Less than the required rest break before shift',
  slot_assignment_ten_hour_break_after: 'Less than the required rest break after shift',
  slot_assignment_weekly_hours_exceed_38: 'Weekly hours over 38',
  slot_assignment_daily_hours_exceed_12: 'Daily hours over 12',
  overlap: 'Double-booking',
  slot_assignment_overlap: 'Double-booking',
  leave: 'Worker on approved leave',
  client_no_go: 'Client has declined this worker',
  slot_candidate_not_feasible: 'Worker not on the feasibility shortlist',
  slot_staff_not_active_for_shift_date: 'Worker not active on the shift date',
  slot_worker_onboarding_blocking_steps_incomplete: 'Onboarding steps incomplete',
  slot_not_assignable: 'Slot is not assignable',
  slot_already_assigned: 'Slot already assigned',
  staff_already_assigned_to_parallel_slot: 'Worker already on a parallel slot',
  // Both agency codes were previously unlabelled, so they reached the
  // coordinator as a formatEnum'd machine token inside a generic
  // "cannot be overridden" message with no exit.
  slot_internal_staff_cannot_be_agency: 'Shift is set aside for agency coverage',
  slot_agency_staff_requires_provider: 'Agency worker needs an agency shift',
};

/**
 * What to actually DO about a blocker that no exception can clear.
 *
 * The hard-blocker panel in AssignShiftForm renders a title and a bare list of
 * labels; overrideUiCopy.body and the exception checkbox are both gated behind
 * managerOverrideRequired. So a non-overridable blocker previously arrived as a
 * noun with no verb — "Worker on approved leave" and nothing else. A coordinator
 * cannot act on that, which is how "blocked with no path forward" happens.
 *
 * Every code in SLOT_ASSIGNMENT_HARD_NEVER_CODES needs an entry here. The
 * accompanying test asserts that, so a new hard-never code cannot ship as a
 * dead end.
 *
 * RCA-1.1 (2026-08-25) turned the flat string map into a structured remedy so
 * the same record can drive three surfaces without drifting:
 *  - `why`    — the reason the gate fired, in coordinator language;
 *  - `action` — the instruction (this is still the plain string every existing
 *               caller receives from slotAssignmentBlockerRemedy);
 *  - `owner`  — who can actually clear it, which is the honest part: three of
 *               the seventeen codes cannot be cleared by the coordinator at all;
 *  - `hrefTemplate` — an in-app destination VERIFIED to exist, or null when the
 *               fix is on the roster itself. A missing screen must read as null
 *               here, never as a plausible-looking link.
 */

/**
 * Who can clear a hard-never blocker.
 *
 * `no_surface` is deliberately a value rather than an omission: `client_no_go`
 * is gated on `client_no_go_workers`, a table with no writer, no management
 * screen anywhere in the repo, and 0 rows in production (measured
 * 2026-08-25, project xaxycjprriboclvfoxbe). Telling a coordinator to "update
 * the no-go list" points at a screen that does not exist.
 */
export type SlotAssignmentRemedyOwner =
  /** The coordinator can act from the roster, or by rostering someone else. */
  | 'coordinator'
  /** Needs a worker-record change first — compliance, onboarding, work rights. */
  | 'people_team'
  /** Nothing in Statura can change this today. Say so rather than inventing a path. */
  | 'no_surface';

export type SlotAssignmentBlockerRemedy = {
  code: string;
  /** Why the gate fired. Never a machine token. */
  why: string;
  /** What to do. Returned verbatim by slotAssignmentBlockerRemedy(). */
  action: string;
  owner: SlotAssignmentRemedyOwner;
  /**
   * Destination that exists today, a governed resolver key, or null when the fix is in place.
   * May carry `{leaveRequestId}` query placeholders — see
   * resolveSlotAssignmentRemedyHref for how unresolved ones are handled.
   */
  hrefTemplate: string | null;
  hrefLabel: string | null;
};

/** What the caller knows about the refusal, used to fill href placeholders. */
export type SlotAssignmentRemedyContext = {
  staffId?: string | null;
  /** Set once the leave-identity read has named the blocking request. */
  leaveRequestId?: string | null;
};

export function slotAssignmentWorkerDetailHref(staffId: string): string {
  return `/people/workers/${encodeURIComponent(staffId)}`;
}

export function slotAssignmentScreeningRegisterHref(): string {
  return '/people/compliance/screening';
}

export function slotAssignmentOnboardingHref(): string {
  return '/people/onboarding';
}

export function slotAssignmentFirstShiftOverrideHref(): string {
  return '/settings/people#policy-first-shift-overrides';
}

const SLOT_ASSIGNMENT_BLOCKER_REMEDY_DETAILS: Record<string, SlotAssignmentBlockerRemedy> = {
  policy_first_shift_override_required: {
    code: 'policy_first_shift_override_required',
    why: 'Your organisation requires a recorded manager override for this outstanding requirement before the worker’s first assignment.',
    action:
      'Record the requirement-specific override and reason in People Settings, then assign again.',
    owner: 'coordinator',
    hrefTemplate: '/settings/people#policy-first-shift-overrides',
    hrefLabel: 'Record a first-shift override',
  },
  leave: {
    code: 'leave',
    why: 'The worker has approved leave covering this shift date.',
    action:
      'Assign a different worker, or cancel or amend this leave request first if the worker has agreed to work.',
    owner: 'coordinator',
    hrefTemplate: '/roster/leave?tab=requests&requestId={leaveRequestId}',
    hrefLabel: 'Open the leave request',
  },
  overlap: {
    code: 'overlap',
    why: 'The worker is already rostered on a shift that overlaps this one.',
    action: 'Assign someone else, or move or unassign the clashing shift first.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_assignment_overlap: {
    code: 'slot_assignment_overlap',
    why: 'The worker is already rostered on a shift that overlaps this one.',
    action: 'Assign someone else, or move or unassign the clashing shift first.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  staff_already_assigned_to_parallel_slot: {
    code: 'staff_already_assigned_to_parallel_slot',
    why: 'The worker already holds another slot on this same shift.',
    action:
      'Assign a different worker to this slot, or free this worker from the parallel slot first.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  client_no_go: {
    code: 'client_no_go',
    why: 'An active no-go record links this client and this worker.',
    /*
     * Corrected 2026-08-25. The previous wording — "update the client no-go
     * list if the decision has changed" — named an action with nowhere to
     * perform it: `client_no_go_workers` is referenced by the feasibility view
     * and the privacy export, and by no writer or screen in any app.
     */
    action:
      'Assign a different worker. Statura has no screen for the client no-go list today, so a decision that has changed has to be removed by an administrator before this worker can be rostered to this client.',
    owner: 'no_surface',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_worker_work_rights_expired_or_invalid: {
    code: 'slot_worker_work_rights_expired_or_invalid',
    why: "The worker's right-to-work evidence is missing, expired, or invalid.",
    action:
      'Record current work rights on the worker profile, then assign again. This cannot be overridden.',
    owner: 'people_team',
    hrefTemplate: '@worker-detail',
    hrefLabel: 'Open the worker profile',
  },
  slot_screenings_invalid_or_expired: {
    code: 'slot_screenings_invalid_or_expired',
    why: 'The worker holds a screening recorded as expired or invalid — a known-bad result, not missing evidence.',
    action:
      'Record a current, valid screening on the worker profile, then assign again. This cannot be overridden.',
    owner: 'people_team',
    hrefTemplate: '/people/compliance/screening',
    hrefLabel: 'Open the screening register',
  },
  slot_worker_onboarding_blocking_steps_incomplete: {
    code: 'slot_worker_onboarding_blocking_steps_incomplete',
    why: 'Onboarding steps marked as blocking are still outstanding for this worker.',
    action: 'Complete the outstanding onboarding steps on the worker profile, then assign again.',
    owner: 'people_team',
    hrefTemplate: '/people/onboarding',
    hrefLabel: 'Open onboarding',
  },
  slot_staff_not_active_for_shift_date: {
    code: 'slot_staff_not_active_for_shift_date',
    why: 'The shift date falls outside the worker’s active employment dates.',
    action: 'Check their start and end dates on the worker profile, or assign a different worker.',
    owner: 'people_team',
    hrefTemplate: '@worker-detail',
    hrefLabel: 'Open the worker profile',
  },
  slot_agency_staff_requires_provider: {
    code: 'slot_agency_staff_requires_provider',
    why: 'The worker is an agency contractor and this shift is not set up for agency coverage.',
    action:
      'Convert the shift to agency demand with its provider, or assign an internal worker instead.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_internal_staff_cannot_be_agency: {
    code: 'slot_internal_staff_cannot_be_agency',
    why: 'The shift is classified as agency demand, so no internal worker can hold it.',
    action: 'Convert the shift to internal cover, or assign an agency worker instead.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_already_assigned: {
    code: 'slot_already_assigned',
    why: 'Another worker already holds this slot.',
    action: 'Unassign the current worker first, or use the replacement flow to swap workers.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_not_assignable: {
    code: 'slot_not_assignable',
    why: 'The slot is in a state that does not take assignments — cancelled, completed, or in progress.',
    action:
      'Reload the roster; if it persists, check whether the shift is cancelled or already in progress.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_candidate_not_feasible: {
    code: 'slot_candidate_not_feasible',
    why: 'The worker is not on the feasibility shortlist that the assignment check reads.',
    action: 'Re-run the feasibility check, or pick a worker from the shortlist.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_assignment_validation_missing_scope: {
    code: 'slot_assignment_validation_missing_scope',
    why: 'The shift has no roster position to check because it was added with a worker already chosen.',
    action:
      'Cancel it, recreate it as Client service with the appropriate shift details and no worker, publish it, then use Fill to assign the worker.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_not_found_for_assignment_validation: {
    code: 'slot_not_found_for_assignment_validation',
    why: 'The slot no longer exists — the board is showing a stale copy.',
    action: 'Reload the roster and try again; the board is showing a stale copy of this slot.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
  slot_assignment_shift_not_found: {
    code: 'slot_assignment_shift_not_found',
    why: 'The shift no longer exists — the board is showing a stale copy.',
    action: 'Reload the roster and try again; the board is showing a stale copy of this shift.',
    owner: 'coordinator',
    hrefTemplate: null,
    hrefLabel: null,
  },
};

/**
 * Fill `{staffId}` / `{leaveRequestId}` from context.
 *
 * A placeholder in the PATH that cannot be filled makes the whole link null —
 * `@worker-detail` with no staff id is not a destination. A
 * placeholder in a QUERY parameter that cannot be filled drops just that
 * parameter, so `/roster/leave?tab=requests&requestId={leaveRequestId}`
 * degrades to the leave list rather than disappearing.
 */
export function resolveSlotAssignmentRemedyHref(
  remedy: SlotAssignmentBlockerRemedy,
  context: SlotAssignmentRemedyContext = {},
): string | null {
  const template = remedy.hrefTemplate;
  if (!template) return null;
  if (template === '@worker-detail') {
    const staffId =
      typeof context.staffId === 'string' && context.staffId.trim() ? context.staffId : null;
    return staffId ? slotAssignmentWorkerDetailHref(staffId) : null;
  }
  if (template === '/people/compliance/screening') {
    return slotAssignmentScreeningRegisterHref();
  }
  if (template === '/people/onboarding') {
    return slotAssignmentOnboardingHref();
  }
  if (template === '/settings/people#policy-first-shift-overrides') {
    return slotAssignmentFirstShiftOverrideHref();
  }
  const values: Record<string, string | null> = {
    staffId: typeof context.staffId === 'string' && context.staffId.trim() ? context.staffId : null,
    leaveRequestId:
      typeof context.leaveRequestId === 'string' && context.leaveRequestId.trim()
        ? context.leaveRequestId
        : null,
  };
  if (
    remedy.code === 'slot_worker_work_rights_invalid' ||
    remedy.code === 'slot_staff_not_active_for_shift_date'
  ) {
    if (!values.staffId) return null;
    return `/people/workers/${encodeURIComponent(values.staffId)}`;
  }
  if (remedy.code === 'slot_screenings_invalid_or_expired') {
    return '/people/compliance/screening';
  }
  if (remedy.code === 'slot_worker_onboarding_blocking_steps_incomplete') {
    return '/people/onboarding';
  }
  const fill = (input: string): string | null => {
    let unresolved = false;
    const filled = input.replace(/\{(\w+)\}/g, (_match, key: string) => {
      const value = values[key];
      if (!value) {
        unresolved = true;
        return '';
      }
      return encodeURIComponent(value);
    });
    return unresolved ? null : filled;
  };

  const [rawPath = '', rawQuery] = template.split('?');
  const path = fill(rawPath);
  if (!path) return null;
  if (!rawQuery) return path;
  const params = rawQuery
    .split('&')
    .map((param) => fill(param))
    .filter((param): param is string => param !== null && param.length > 0);
  return params.length > 0 ? `${path}?${params.join('&')}` : path;
}

/** The structured remedy for a blocker, or null when it is not hard-never. */
export function slotAssignmentBlockerRemedyDetail(
  code: string,
): SlotAssignmentBlockerRemedy | null {
  const base = slotAssignmentBlockerBaseCode(code);
  return SLOT_ASSIGNMENT_BLOCKER_REMEDY_DETAILS[base] ?? null;
}

export type ResolvedSlotAssignmentBlockerRemedy = SlotAssignmentBlockerRemedy & {
  href: string | null;
};

/**
 * Structured remedies for a blocker list, in order, de-duplicated by the
 * instruction so `overlap` + `slot_assignment_overlap` render once.
 */
export function slotAssignmentBlockerRemedyDetails(
  codes: readonly string[],
  context: SlotAssignmentRemedyContext = {},
): ResolvedSlotAssignmentBlockerRemedy[] {
  const out: ResolvedSlotAssignmentBlockerRemedy[] = [];
  const seen = new Set<string>();
  for (const code of codes) {
    const remedy = slotAssignmentBlockerRemedyDetail(code);
    if (!remedy || seen.has(remedy.action)) continue;
    seen.add(remedy.action);
    out.push({ ...remedy, href: resolveSlotAssignmentRemedyHref(remedy, context) });
  }
  return out;
}

/**
 * Instruction for a blocker, or null when the code routes to the manager
 * exception path (which renders its own guidance) or is unmodelled.
 */
export function slotAssignmentBlockerRemedy(code: string): string | null {
  return slotAssignmentBlockerRemedyDetail(code)?.action ?? null;
}

/** Distinct remedies for a blocker list, in order, de-duplicated. */
export function slotAssignmentBlockerRemedies(codes: readonly string[]): string[] {
  const out: string[] = [];
  for (const code of codes) {
    const remedy = slotAssignmentBlockerRemedy(code);
    if (remedy && !out.includes(remedy)) out.push(remedy);
  }
  return out;
}

export type SlotAssignmentHardConstraintClassification =
  | { kind: 'overridable'; codes: string[]; baseCodes: string[] }
  | { kind: 'hard'; codes: string[]; baseCodes: string[]; hardCodes: string[] };

function messageFrom(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error && 'message' in error) {
    return String((error as { message?: unknown }).message ?? '');
  }
  return String(error ?? '');
}

/** Strip optional `:detail` suffixes (e.g. role-cert lists, policy counts). */
export function slotAssignmentBlockerBaseCode(code: string): string {
  return code.split(':')[0]?.trim().toLowerCase() ?? '';
}

export function isKnownSlotAssignmentBlockerCode(code: string): boolean {
  return Object.hasOwn(SLOT_ASSIGNMENT_BLOCKER_LABELS, slotAssignmentBlockerBaseCode(code));
}

const ASSIGNMENT_DATA_PROBLEM_LABEL =
  "We couldn't assign this worker because of a data problem with the shift.";

export function slotAssignmentBlockerLabel(code: string): string {
  const base = slotAssignmentBlockerBaseCode(code);
  if (base === 'shift_target_missing')
    return 'This shift has no client. Add the client, then assign the worker.';
  const known = Object.hasOwn(SLOT_ASSIGNMENT_BLOCKER_LABELS, base)
    ? SLOT_ASSIGNMENT_BLOCKER_LABELS[base]
    : undefined;
  if (known) return known;
  if (/^[A-Z0-9_]+$/.test(code) && code !== 'ASSIGNMENT_CONSTRAINT_VIOLATION')
    return formatEnum(code);
  // A bare token in the database's own blocker namespace (`slot_...`) is a named
  // blocker this map does not list yet: keep its words. Anything else lower-case
  // (free text, an unknown token) stays behind the generic message.
  if (/^slot_[a-z0-9_]+$/.test(code)) return formatEnum(base || code);
  return ASSIGNMENT_DATA_PROBLEM_LABEL;
}

export function formatBlockerLabel(code: string, commandId?: string): string {
  const label = slotAssignmentBlockerLabel(code);
  return label === ASSIGNMENT_DATA_PROBLEM_LABEL && commandId
    ? `${label} Reference: ${commandId}`
    : label;
}

/**
 * Parse `slot_assignment_hard_constraint_failed:code1,code2[:detail],...`.
 * Returns null when the message is not that machine code.
 *
 * Fail closed on ambiguous input: empty/whitespace-only code lists, empty
 * tokens after split (e.g. `code1,,code2`), a captured segment that does not
 * cleanly tokenise into non-empty codes, OR any non-whitespace content on a
 * later line (the commit RPC emits the codes as ONE comma line via
 * array_to_string, so multi-line content could hide a hard code after a
 * newline) → null (→ not overridable). Over-restricting is fail-safe; the
 * override RPC re-derives and remains the authoritative gate regardless.
 */
export function parseSlotAssignmentHardConstraintCodes(error: unknown): string[] | null {
  const message = messageFrom(error).trim();
  if (!message) return null;
  const marker = message.search(/slot_assignment_hard_constraint_failed:/i);
  if (marker < 0) return null;
  const after = message.slice(marker).replace(/^slot_assignment_hard_constraint_failed:/i, '');
  // Any non-whitespace after the first line is unexpected — fail closed rather
  // than silently drop a hard code hidden after a newline (adversarial audit).
  const newlineIdx = after.search(/[\r\n]/);
  if (newlineIdx >= 0 && /\S/.test(after.slice(newlineIdx))) return null;
  const segment = (newlineIdx >= 0 ? after.slice(0, newlineIdx) : after).trim();
  if (!segment) return null;

  const parts = segment.split(',');
  const codes: string[] = [];
  for (const part of parts) {
    const trimmed = part.trim();
    // Empty token after split is ambiguous — do not silently drop and continue.
    if (!trimmed) return null;
    codes.push(trimmed);
  }
  if (codes.length === 0) return null;
  return codes;
}

function parseMachineCodeList(error: unknown, machineCode: string): string[] | null {
  const message = messageFrom(error).trim();
  if (!message) return null;
  const marker = message.toLowerCase().indexOf(`${machineCode.toLowerCase()}:`);
  if (marker < 0) return null;
  const after = message.slice(marker + machineCode.length + 1);
  const newlineIdx = after.search(/[\r\n]/);
  if (newlineIdx >= 0 && /\S/.test(after.slice(newlineIdx))) return null;
  const segment = (newlineIdx >= 0 ? after.slice(0, newlineIdx) : after).trim();
  if (!segment) return null;

  const codes: string[] = [];
  for (const part of segment.split(',')) {
    const code = part.trim();
    if (!code) return null;
    codes.push(code);
  }
  return codes.length > 0 ? codes : null;
}

/**
 * Parse blocker metadata from normal assignment, rejected exceptional
 * assignment, or the legacy direct first-shift trigger. This is presentation
 * metadata only; the controlled exceptional RPC still owns policy decisions.
 */
export function parseRosterAssignmentBlockerCodes(error: unknown): string[] | null {
  const slotCodes = parseSlotAssignmentHardConstraintCodes(error);
  if (slotCodes) return slotCodes;

  const rejectedCodes = parseMachineCodeList(
    error,
    'roster_assignment_exception_non_overrideable_blockers',
  );
  if (rejectedCodes) return rejectedCodes;

  const policyRequirementKeys = parseMachineCodeList(error, 'policy_first_shift_override_required');
  if (policyRequirementKeys) {
    return policyRequirementKeys.map((key) => `policy_first_shift_override_required:${key}`);
  }

  return messageFrom(error).toLowerCase().includes('first_shift_onboarding_not_ready')
    ? ['slot_worker_first_shift_readiness_not_ready']
    : null;
}

export function rosterAssignmentSupportsReactiveManagerReview(codes: string[]): boolean {
  return (
    codes.length > 0 &&
    codes.every((code) =>
      REACTIVE_MANAGER_REVIEW_CANDIDATE_CODES.has(slotAssignmentBlockerBaseCode(code)),
    )
  );
}

export function isSlotAssignmentBlockerOverridable(code: string): boolean {
  const base = slotAssignmentBlockerBaseCode(code);
  if (!base) return false;
  if (SLOT_ASSIGNMENT_HARD_NEVER_CODES.has(base)) return false;
  return SLOT_ASSIGNMENT_OVERRIDABLE_CODES.has(base);
}

/**
 * Whether the agency classification blocker has a safe inline exit. Removing
 * that one blocker must leave only provider-policy-approved exceptions (from
 * exact preflight), or the deliberately narrow reactive first-shift review.
 * Unknown/non-overridable codes always fail closed.
 */
export function canRouteAgencyConversionWithAssignment(input: {
  blockerCodes: string[];
  exactOverrideableBlockerCodes?: string[];
  exactNonOverrideableBlockerCodes?: string[];
  reactiveManagerReviewAvailable?: boolean;
}): boolean {
  const hasAgencyMismatch = input.blockerCodes.some(
    (code) => slotAssignmentBlockerBaseCode(code) === 'slot_internal_staff_cannot_be_agency',
  );
  if (!hasAgencyMismatch) return false;

  const remaining = input.blockerCodes.filter(
    (code) => slotAssignmentBlockerBaseCode(code) !== 'slot_internal_staff_cannot_be_agency',
  );
  if (remaining.some((code) => !isSlotAssignmentBlockerOverridable(code))) return false;
  if (remaining.length === 0) return true;

  if (
    Array.isArray(input.exactOverrideableBlockerCodes) &&
    Array.isArray(input.exactNonOverrideableBlockerCodes)
  ) {
    const exactOverrideable = new Set(
      input.exactOverrideableBlockerCodes.map(slotAssignmentBlockerBaseCode),
    );
    const onlyAgencyIsExactNonOverrideable = input.exactNonOverrideableBlockerCodes.every(
      (code) => slotAssignmentBlockerBaseCode(code) === 'slot_internal_staff_cannot_be_agency',
    );
    if (
      onlyAgencyIsExactNonOverrideable &&
      remaining.every((code) => exactOverrideable.has(slotAssignmentBlockerBaseCode(code)))
    ) {
      return true;
    }
  }

  return (
    input.reactiveManagerReviewAvailable === true ||
    rosterAssignmentSupportsReactiveManagerReview(remaining)
  );
}

/**
 * Classify a hard-constraint failure for UI routing.
 * - overridable: every code is in the app-side overridable set → open override UI
 * - hard: any code is never-overridable or unknown → fail hard, never override
 */
export function classifySlotAssignmentHardConstraint(
  error: unknown,
): SlotAssignmentHardConstraintClassification | null {
  const codes = parseSlotAssignmentHardConstraintCodes(error);
  if (!codes) return null;
  const baseCodes = codes.map(slotAssignmentBlockerBaseCode);
  const hardCodes = codes.filter((code) => !isSlotAssignmentBlockerOverridable(code));
  if (hardCodes.length > 0) {
    return {
      kind: 'hard',
      codes,
      baseCodes,
      hardCodes: hardCodes.map(slotAssignmentBlockerBaseCode),
    };
  }
  return { kind: 'overridable', codes, baseCodes };
}

const COMPLIANCE_OVERRIDE_BASE_CODES = new Set<string>(SLOT_ASSIGNMENT_COMPLIANCE_OVERRIDE_CODES);
const OPERATIONAL_OVERRIDE_BASE_CODES = new Set<string>(SLOT_ASSIGNMENT_OPERATIONAL_OVERRIDE_CODES);

/** Which overridable families are present in a code list (base-stripped). */
export type SlotAssignmentOverrideCopyKind = 'compliance' | 'operational' | 'mixed';

/**
 * Classify overridable codes for UI/error copy.
 * Unknown / hard codes are ignored here — callers should only pass overridable sets.
 */
export function classifySlotAssignmentOverrideCopyKind(
  codes: string[],
): SlotAssignmentOverrideCopyKind {
  let hasCompliance = false;
  let hasOperational = false;
  for (const code of codes) {
    const base = slotAssignmentBlockerBaseCode(code);
    if (!base) continue;
    if (COMPLIANCE_OVERRIDE_BASE_CODES.has(base)) hasCompliance = true;
    if (OPERATIONAL_OVERRIDE_BASE_CODES.has(base)) hasOperational = true;
  }
  if (hasCompliance && hasOperational) return 'mixed';
  if (hasOperational) return 'operational';
  // Compliance-only, or empty/unknown overridable codes → evidence-pending framing.
  return 'compliance';
}

/**
 * Human message when plain assign failed only on overridable codes.
 * Branches on compliance vs operational vs mixed — never claims "clearance
 * verified elsewhere" for a fatigue/availability-only override.
 */
export function evidencePendingOverrideMessage(codes: string[]): string {
  const labels = Array.from(new Set(codes.map(slotAssignmentBlockerLabel)));
  const list = labels.length > 0 ? ` Outstanding: ${labels.join(', ')}.` : '';
  const kind = classifySlotAssignmentOverrideCopyKind(codes);

  if (kind === 'operational') {
    return (
      'This assignment breaches a scheduling/fatigue rule (rest break, weekly ' +
      'or daily hours, or availability). Record why management accepts the operational risk. Short-rest ' +
      'or hours exceptions may trigger overtime, penalty, or enterprise-agreement ' +
      'payments and require payroll/fatigue evidence. The override does not waive any ' +
      'employee agreement or payment entitlement.' +
      list
    );
  }
  if (kind === 'mixed') {
    return (
      "This assignment has both compliance evidence that isn't in Statura yet " +
      'and a scheduling/fatigue or availability breach. Record a reason to proceed. ' +
      'The controlled override will recheck current provider policy; you also accept any ' +
      'operational risk that policy permits.' +
      list
    );
  }
  return (
    "This worker's compliance evidence isn't in Statura yet. Request an audited manager " +
    'override; the controlled assignment check will revalidate current provider policy.' +
    list
  );
}

/** Title/body/checkbox labels for the assign-form override affordance. */
export function slotAssignmentOverrideUiCopy(codes: string[]): {
  kind: SlotAssignmentOverrideCopyKind;
  title: string;
  body: string;
  checkboxLabel: string;
  reasonPlaceholder: string;
} {
  // Empty codes (shortlist overrideEligible without machine codes): cover both
  // honestly so we never claim clearance-verified-elsewhere for fatigue-only.
  if (codes.length === 0) {
    return {
      kind: 'mixed',
      title: 'This assignment requires a manager override',
      body:
        'Listed blockers may be compliance evidence not yet in Statura and/or a scheduling, ' +
        'fatigue, or availability rule breach. Request an audited review and record a reason. ' +
        'The controlled assignment check will revalidate every blocker against current provider policy.',
      checkboxLabel: 'Request an audited manager override for this exceptional assignment.',
      reasonPlaceholder:
        'e.g. Verified outside Statura and/or accepted operational rest/hours/availability risk; detail the decision.',
    };
  }

  const kind = classifySlotAssignmentOverrideCopyKind(codes);
  if (kind === 'operational') {
    return {
      kind,
      title: 'This assignment breaches a scheduling or fatigue rule',
      body:
        'Rest break, weekly/daily hours, or availability is outside the normal roster rules. ' +
        'Provider policy may allow the roster to proceed, but it does not waive any employee ' +
        'agreement or payment entitlement. Short-rest or hours exceptions may trigger overtime, ' +
        'penalty, or enterprise-agreement payments. Statura records the exception for payroll/' +
        'fatigue evidence and independent payroll review.',
      checkboxLabel: 'Request an audited manager override for this operational exception.',
      reasonPlaceholder:
        'e.g. Accepted short rest / weekly hours / availability conflict for continuity of care; fatigue risk reviewed.',
    };
  }
  if (kind === 'mixed') {
    return {
      kind,
      title: 'Compliance evidence pending and scheduling rule breach',
      body:
        "This worker's compliance evidence isn't fully in Statura yet, and the assignment also " +
        'breaches a scheduling/fatigue or availability rule. Request an audited review and ' +
        'record a reason. The controlled assignment check will revalidate current provider policy.',
      checkboxLabel:
        'Request an audited manager override for this mixed compliance and operational exception.',
      reasonPlaceholder:
        'e.g. Screening/training evidence sighted outside Statura; also accepted operational hours/rest risk; detail both.',
    };
  }
  return {
    kind,
    title: "This worker's compliance evidence isn't in Statura yet",
    body:
      'Request an audited manager override and record the evidence-pending reason. The controlled ' +
      'assignment check will revalidate current provider policy; never treat this as proof of clearance.',
    checkboxLabel: 'Request an audited manager override for this evidence-pending assignment.',
    reasonPlaceholder:
      'e.g. Screening / training evidence sighted in provider records; evidence upload pending.',
  };
}

/**
 * Short reason lead for each hard-never branch that has no bespoke paragraph.
 * The instruction itself always comes from the remedy registry, so a copy edit
 * happens in one place.
 */
/*
 * `satisfies` rather than a `Record<string, string>` annotation: the annotation
 * widened the key type to `string`, so every lookup came back
 * `string | undefined` and a missing reason would have reached a coordinator as
 * the literal text "undefined". Keeping the literal keys makes the lookup exact
 * and lets the loop below prove, at compile time, that every hard-refusal code
 * has a reason.
 */
const HARD_REFUSAL_REASONS = {
  leave: 'this worker is on approved leave.',
  client_no_go: 'the client has declined this worker.',
  slot_already_assigned: 'this slot already has a worker.',
  staff_already_assigned_to_parallel_slot:
    'this worker is already on a parallel slot for this shift.',
  slot_not_assignable: 'this slot is completed, cancelled, or otherwise locked.',
  slot_staff_not_active_for_shift_date: 'this worker is not active on the shift date.',
  slot_worker_onboarding_blocking_steps_incomplete:
    'this worker still has incomplete onboarding steps that cannot be overridden from the roster.',
  slot_candidate_not_feasible: 'this worker is not on the feasibility shortlist for the slot.',
} satisfies Record<string, string>;

function refusalWithRemedy(reason: string, code: string): string {
  const action = slotAssignmentBlockerRemedy(code);
  return action ? `Can't assign: ${reason} ${action}` : `Can't assign: ${reason}`;
}

export type HardSlotAssignmentConstraintMessageOptions = {
  errorCode?: string | null;
  commandId?: string | null;
};

export function hardSlotAssignmentConstraintMessage(
  codes: string[],
  options?: HardSlotAssignmentConstraintMessageOptions,
): string {
  const hard = codes
    .map(slotAssignmentBlockerBaseCode)
    .filter(
      (base) =>
        SLOT_ASSIGNMENT_HARD_NEVER_CODES.has(base) || !SLOT_ASSIGNMENT_OVERRIDABLE_CODES.has(base),
    );
  const unique = Array.from(
    new Set(hard.length > 0 ? hard : codes.map(slotAssignmentBlockerBaseCode)),
  );

  if (
    unique.length > 0 &&
    unique.every((code) => code === 'policy_first_shift_override_required')
  ) {
    return 'Record a manager override and reason for the outstanding requirement in Settings > People > Policies > First-shift overrides, then assign again.';
  }

  if (unique.length === 0) {
    const codeSuffix = options?.errorCode ? ` (code ${options.errorCode})` : '';
    return (
      `Can't assign: the assignment was refused but no blocker was named${codeSuffix}. ` +
      'Refresh the roster and try again. If it repeats, report the code to Statura support.'
    );
  }

  /**
   * Double-booking names its own exit, and says explicitly that the other
   * blockers are not what is refusing.
   *
   * A coordinator reported (2026-07-30) reading "Double-booking, Daily hours over
   * 12, Weekly hours over 38" under a blanket "cannot be manager-overridden",
   * while the award preflight underneath offered a manager override for the hours.
   * Hours and rest ARE overridable on their own; the overlap is not. Without
   * saying so, the drawer reads as self-contradictory and the real fix — free the
   * worker from the shift they already hold — is never stated.
   */
  if (unique.some((c) => c === 'overlap' || c === 'slot_assignment_overlap')) {
    /**
     * Derived from the FULL code list, not `unique` — `unique` has already been
     * narrowed to the hard codes, so the overridable hours/rest codes the
     * coordinator can see on screen are absent from it. Reading it there made the
     * clarifying note silently never appear.
     */
    const alsoListed = codes
      .map(slotAssignmentBlockerBaseCode)
      .filter((c) => c && c !== 'overlap' && c !== 'slot_assignment_overlap');
    const hoursNote =
      alsoListed.length > 0
        ? ' The hours and rest warnings listed above could be manager-overridden on their own — the double-booking cannot, so it is what is refusing this assignment.'
        : '';
    return (
      "Can't assign: this worker is already rostered on another shift at this time, and a " +
      'double-booking cannot be manager-overridden. Unassign them from the conflicting shift ' +
      'first, or choose a worker who is free.' +
      hoursNote
    );
  }
  /*
   * RCA-1.1 (2026-08-25): eight of the seventeen hard-never codes reached the
   * coordinator through this function as a reason with no verb — "Can't assign:
   * this worker is on approved leave." and nothing else. The remedy panel added
   * in 10ace831 only renders inside AssignShiftForm's preflight; THIS string is
   * what the server actions return, so it is what dispatch, replace, bulk fill
   * and every toast show. Each branch now carries its registry instruction, so
   * the refusal and the remedy cannot drift apart.
   */
  for (const code of [
    'leave',
    'client_no_go',
    'slot_already_assigned',
    'staff_already_assigned_to_parallel_slot',
    'slot_not_assignable',
    'slot_staff_not_active_for_shift_date',
    'slot_worker_onboarding_blocking_steps_incomplete',
    'slot_candidate_not_feasible',
  ] as const satisfies readonly (keyof typeof HARD_REFUSAL_REASONS)[]) {
    // `satisfies` above proves at compile time that every code listed here has
    // a reason, so the lookup is exact rather than possibly-undefined. Adding a
    // code with no reason text now fails the build instead of surfacing
    // "undefined" to a coordinator as the explanation for a hard refusal.
    if (unique.includes(code)) return refusalWithRemedy(HARD_REFUSAL_REASONS[code], code);
  }
  /**
   * Agency mismatch is a COST-CLASSIFICATION conflict, not missing evidence, so
   * it is correctly un-overridable — but it does have a working exit, and until
   * 2026-07-30 this message did not name it. Choosing another worker never helps:
   * the blocker depends on the shift, not the person, so every internal worker
   * fails identically (Medea Park: 0 of 122 agency slots ever filled).
   */
  if (unique.includes('slot_internal_staff_cannot_be_agency')) {
    const otherHardCodes = unique.filter((code) => code !== 'slot_internal_staff_cannot_be_agency');
    const otherHardNote =
      otherHardCodes.length > 0
        ? ` The assignment is also blocked by: ${otherHardCodes
            .map(slotAssignmentBlockerLabel)
            .join(
              '; ',
            )}. Converting the coverage classification will not clear those worker or shift blockers.`
        : '';
    return (
      "Can't assign while this shift is set aside for agency coverage. Convert it to internal cover " +
      'using the in-panel control when it is offered, or from the Fill tab. A different internal ' +
      'worker will not help clear the agency classification.' +
      otherHardNote
    );
  }
  if (unique.includes('slot_agency_staff_requires_provider')) {
    return "Can't assign: this is an agency worker and the shift is not set up for agency coverage. Set the shift to agency cover with its provider first, or choose one of your own staff.";
  }

  const labels = unique.map((code) =>
    formatBlockerLabel(
      codes.find((raw) => slotAssignmentBlockerBaseCode(raw) === code) ?? code,
      options?.commandId ?? undefined,
    ),
  );
  /*
   * The generic tail used to end at "cannot be overridden from the roster" — a
   * refusal with no verb for the four structural codes that land here
   * (missing scope, slot not found, shift not found, work rights) and for any
   * unmodelled code. Append whatever instructions the registry holds.
   */
  const tailRemedies = slotAssignmentBlockerRemedies(unique);
  const tailAdvice =
    tailRemedies.length > 0
      ? ` ${tailRemedies.join(' ')}`
      : ' Resolve them or choose another worker.';
  return `Can't assign: ${labels.join(', ')}. These blockers cannot be overridden from the roster.${tailAdvice}`;
}

export function authoritativeSlotAssignmentConstraintMessage(
  codes: string[],
  managerReviewAvailable: boolean,
): string {
  const labels = Array.from(new Set(codes.map(slotAssignmentBlockerLabel)));
  const blockerSummary = labels.length > 0 ? `: ${labels.join('; ')}` : '';
  return managerReviewAvailable
    ? `The final assignment check blocked this worker${blockerSummary}. Request an audited manager override; current provider policy will be revalidated.`
    : `The final assignment check blocked this worker${blockerSummary}. Resolve the blockers or choose another worker.`;
}
